import { useState } from 'react';
import { BookOpen, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trainerContentApi, type CurriculumTopic } from '@/lib/api/trainer';

interface Props {
  batchId: string;
  topic: CurriculumTopic;
  /** Lifts the updated topic back so the curriculum tree stays in step. */
  onChanged?: (topic: CurriculumTopic) => void;
}

/**
 * Formats the release moment for a trainer reading the screen.
 *
 * Date AND time, because the whole reason this is a timestamp is that "was
 * this released before or after Thursday's deadline" is a question worth
 * being able to answer at a glance.
 */
function formatReleasedAt(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The trainer's switch for this topic's editorials.
 *
 * By default a student reads the walkthrough only after an accepted
 * submission — right for the catalogue, backwards for a taught lab, where the
 * student who cannot get an AC is the one the walkthrough was written for.
 * Releasing is the debrief: it hands the walkthroughs to everyone in THIS
 * batch, and to nobody outside it.
 *
 * Releasing is confirmed because it is immediately visible to students and
 * cannot be un-seen; withdrawing is not, because it only ever takes something
 * away and is itself the undo.
 */
export function EditorialReleaseControl({ batchId, topic, onChanged }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const released = !!topic.editorial_released_at;

  const apply = async (next: boolean) => {
    setBusy(true);
    try {
      const res = await trainerContentApi.setEditorialRelease(batchId, topic.id, next);
      onChanged?.(res.data);
      toast.success(
        next
          ? 'Editorials released — everyone in this batch can read them now'
          : 'Editorials locked again — only students who solved the problem can read them',
      );
    } catch {
      toast.error(next ? 'Failed to release the editorials' : 'Failed to lock the editorials');
    } finally {
      setBusy(false);
    }
  };

  return (
    // flex-wrap + min-w-0: at 390px the label and the button stack instead of
    // pushing the pane wider. Nothing here has a fixed width.
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#ececec] bg-[#fafafa] px-3 py-2.5">
      <div className="flex min-w-0 items-start gap-2.5">
        <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#6b7280]" />
        <div className="min-w-0">
          <p className="text-sm font-medium">Editorials</p>
          <p className="text-xs text-[#6b7280]">
            {released ? (
              <>
                Released to this batch on{' '}
                <span className="font-medium text-[#111827]">
                  {formatReleasedAt(topic.editorial_released_at!)}
                </span>
                . Every enrolled student can read them.
              </>
            ) : (
              'Only students who have solved the problem can read them.'
            )}
          </p>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <span
          className={
            'rounded px-1.5 py-0.5 text-[10px] font-medium ' +
            (released ? 'bg-green-50 text-green-700' : 'bg-[#f3f4f6] text-[#6b7280]')
          }
        >
          {released ? 'Released' : 'Locked'}
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => (released ? apply(false) : setConfirmOpen(true))}
          className="inline-flex items-center gap-1 rounded border border-[#ececec] bg-white px-2 py-1 text-xs font-medium text-[#6b7280] transition-colors hover:bg-[#fafafa] hover:text-[#111827] disabled:opacity-50"
        >
          {released ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
          {released ? 'Lock again' : 'Release'}
        </button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Release the editorials for “{topic.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Every student enrolled in this batch will be able to read the full walkthrough
              for this topic's problems straight away, whether or not they have solved them.
              Students in other batches are unaffected. You can lock them again afterwards,
              but anything already read cannot be un-read.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            {/* Radix's Action closes the dialog itself — no need to also flip
                confirmOpen here. */}
            <AlertDialogAction onClick={() => apply(true)}>Release editorials</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
